import { useState } from 'react';
import { Ban } from 'lucide-react';
import type { RecentEventEntry } from '@/lib/api';

const DEFAULT_COLLAPSED_COUNT = 2;
const DEFAULT_EXPANDED_COUNT = 5;

const TYPE_LETTER: Record<RecentEventEntry['type'], string> = {
  canyon_storm: 'C',
  desert_storm: 'D',
  special_ingame: 'S',
};

type Look = {
  char: React.ReactNode;
  bg: string;
  text: string;
  style?: React.CSSProperties;
};

// Diagonal red/green stripes — used for "picked but no-show" so the cell
// stays anchored to the "picked" green history while screaming that the
// player didn't show up. For subs we swap the green stripe for sky-blue.
const noShowStripes = (role: RecentEventEntry['role']): React.CSSProperties => ({
  backgroundImage:
    role === 'sub'
      ? 'repeating-linear-gradient(45deg, rgb(153 27 27 / 0.9) 0 4px, rgb(3 105 161 / 0.7) 4px 8px)'
      : 'repeating-linear-gradient(45deg, rgb(153 27 27 / 0.9) 0 4px, rgb(4 120 87 / 0.7) 4px 8px)',
});

function look(e: RecentEventEntry): Look {
  if (e.redCarded && e.outcome !== 'picked') {
    // Red-carded players are excluded from selection, so their non-pick
    // is not a fairness debt — surface that explicitly with a card mark.
    return { char: '▮', bg: 'bg-rose-950/80', text: 'text-rose-200' };
  }
  switch (e.outcome) {
    case 'picked':
      if (e.attendance === 'no_show') {
        return {
          char: (
            <Ban
              size={12}
              strokeWidth={3.5}
              className="drop-shadow-[0_0_2px_rgba(0,0,0,0.9)]"
            />
          ),
          bg: '',
          text: 'text-white',
          style: noShowStripes(e.role),
        };
      }
      if (e.role === 'sub') {
        return { char: '✓', bg: 'bg-sky-700/70', text: 'text-sky-100' };
      }
      return { char: '✓', bg: 'bg-emerald-700/70', text: 'text-emerald-100' };
    case 'applied_not_picked':
      return { char: '×', bg: 'bg-red-800/70', text: 'text-red-100' };
    case 'not_applied':
      return { char: '○', bg: 'bg-slate-700/70', text: 'text-slate-300' };
  }
}

function tooltipFor(e: RecentEventEntry): string {
  const type = e.type === 'canyon_storm' ? 'Canyon Storm' : 'Desert Storm';
  const slot = e.slot ? ` slot ${e.slot}` : '';
  const outcome =
    e.redCarded && e.outcome !== 'picked'
      ? e.outcome === 'applied_not_picked'
        ? 'applied, red-carded (excluded)'
        : 'not registered, red-carded'
      : e.outcome === 'picked'
        ? (() => {
            const base = e.role === 'sub' ? 'picked as sub' : 'picked';
            if (e.attendance === 'no_show') return `${base}, no-show`;
            if (e.attendance === 'late') return `${base}, late`;
            return base;
          })()
        : e.outcome === 'applied_not_picked'
          ? 'applied, not picked'
          : 'not registered';
  return `${e.eventDate} · ${type}${slot} — ${outcome}`;
}

export function RecentEventsChip({
  events,
  collapsedCount = DEFAULT_COLLAPSED_COUNT,
  expandedCount = DEFAULT_EXPANDED_COUNT,
  defaultOpen = false,
}: {
  events: RecentEventEntry[];
  collapsedCount?: number;
  expandedCount?: number;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (!events.length) return null;
  const shown = open
    ? events.slice(0, expandedCount)
    : events.slice(0, collapsedCount);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setOpen((v) => !v);
      }}
      className="inline-flex overflow-hidden rounded border border-slate-800 text-[10px] font-semibold tabular-nums leading-none"
      title={open ? 'Click to collapse' : 'Click to expand history'}
    >
      {shown.map((ev, i) => {
        const l = look(ev);
        return (
          <span
            key={`${ev.eventDate}-${ev.type}-${i}`}
            title={tooltipFor(ev)}
            style={l.style}
            className={`inline-flex items-center gap-[1px] px-1 py-[3px] ${l.bg} ${l.text} ${
              i > 0 ? 'border-l border-slate-950/60' : ''
            }`}
          >
            <span>{l.char}</span>
            {open && (
              <span className="opacity-80">
                {TYPE_LETTER[ev.type]}
                {ev.slot ?? ''}
              </span>
            )}
          </span>
        );
      })}
    </button>
  );
}
